import moment from 'moment';
import {
  API_RESPONSE_CODE,
  ATTENDEES_STATUS,
  CLICK_ACTIONS,
  COMPLETED_STATUS,
  KYC_DOCUMENT_APPROVAL_STATUS,
  LEAVE_STATUSES,
  REIMBURSEMENT_STATUS_TYPES,
  STORAGE_KEY,
} from './Constant';
import {
  DocumentType,
  NoteType,
  LoggedUserType,
  CampaignDetailType,
  UserType,
  ZoneRequestObj,
  MapOptionType,
  SelectOptionType,
  ColumnHeaderType,
  LocationFilterInitialState,
} from './Types';

import UserDefault from '../assets/img/user_default.svg';
import { toLatLon } from './UtmToLatLng';
import { loginMockData } from './mockData';
import {
  DISTRICT_ACCESS_ROLES,
  PINCODES_ACCESS_ROLES,
  STATE_ACCESS_ROLES,
  ZONE_ACCESS_ROLES,
} from './RBAC';

export const isValidString = (value?: string | number | null) => {
  if (
    !value ||
    value === 'null' ||
    value === 'undefined' ||
    String(value).replaceAll(' ', '').length === 0
  ) {
    return false;
  }
  return true;
};
const replaceStringAt = (text: string, index: number, replaceWith: string) => {
  if (index > text.length - 1) return text;
  return text.substring(0, index) + replaceWith + text.substring(index + 1);
};

export const arrayToFormattedString = (array: string[]) => {
  if (!array || array.length === 0) {
    return '-';
  }
  let commaSeparated = array.join(', ');
  if (array.length > 1) {
    commaSeparated = replaceStringAt(
      commaSeparated,
      commaSeparated.lastIndexOf(','),
      ' &'
    );
  }
  return commaSeparated;
};

export const stringToFormattedString = (text: string) => {
  if (!isValidString(text)) {
    return '-';
  }
  if (text.indexOf(',') === -1) {
    return text;
  } else {
    return replaceStringAt(text, text.lastIndexOf(','), ' &');
  }
};

export const checkNullValue = (value?: string | number) => {
  if (!isValidString(value)) {
    return '-';
  }
  return value;
};
export const checkNumberValue = (value?: string | number) => {
  const number = Number(value);
  if (!isNaN(number) && value !== '' && value !== null) {
    return number;
  }
  return '-';
};
export const getBlankValueIfNUll = (value?: string | number | null) => {
  if (!isValidString(value)) {
    return '';
  }
  return value;
};

export const formattedDate = (inputDate: string) => {
  if (!isValidString(inputDate)) return '-';
  return moment(inputDate).format('DD-MM-YYYY');
};
export const formattedDateInLocal = (inputDate: string) => {
  if (!isValidString(inputDate)) return '-';
  return moment.utc(inputDate).local().format('DD-MM-YYYY');
};
export const formattedDateWithFormat = (
  inputDate: string,
  dateFormat: string
) => {
  if (!isValidString(inputDate) || !isValidString(dateFormat)) return '-';
  return moment(inputDate).format(dateFormat);
};
export const formattedDateAsUTC = (inputDate: string, dateFormat: string) => {
  if (!isValidString(inputDate) || !isValidString(dateFormat)) return '-';
  return moment.utc(inputDate).format(dateFormat);
};
export const formattedDateWithFormatInLocal = (
  inputDate: string,
  dateFormat: string
) => {
  if (!isValidString(inputDate) || !isValidString(dateFormat)) return '-';
  return moment.utc(inputDate).local().format(dateFormat);
};

export const nameValueGetter = (data: any) => {
  return checkNullValue(
    `${getBlankValueIfNUll(data.firstName)} ${getBlankValueIfNUll(data.lastName)}`
  );
};

export const noOfDaysValueGetter = (data: any) => {
  return data?.endDate && data.startDate
    ? checkNullValue(
        `${moment(data.endDate).diff(moment(data.startDate), 'days') + 1}`
      )
    : '-';
};

export const nameValueGetterSmall = (data: any) => {
  return checkNullValue(
    `${getBlankValueIfNUll(data.firstname)} ${getBlankValueIfNUll(data.lastname)}`
  );
};
export const blankValueGetter = (data: any, field?: string) => {
  return checkNullValue(data[field as keyof any]);
};

export const dateValueGetter = (data: any, field?: string) => {
  return formattedDate(String(data[field as keyof any]));
};
export const dateValueGetterWithTime = (data: any, field?: string) => {
  return formattedDateWithFormat(
    String(data[field as keyof any]),
    'DD-MM-YYYY hh:mm a'
  );
};

export const addSeparator = (text1: string, text2: any) => {
  if (isValidString(text1) && isValidString(text2)) {
    return text1 + ', ' + text2;
  } else if (isValidString(text2)) {
    return text2;
  } else if (isValidString(text1)) {
    return text1;
  } else {
    return '-';
  }
};

export const getCompletedStatus = (data: any, field?: string) => {
  const value = data[field as keyof any];
  if (!isValidString(value)) {
    return COMPLETED_STATUS.NOT_COMPLETED;
  } else {
    return COMPLETED_STATUS.COMPLETED;
  }
};

export const getCompletedClassNameGetter = (data: any, field?: string) => {
  const value = data[field as keyof any];
  if (!isValidString(value)) {
    return 'text-not-completed ';
  } else {
    return 'text-completed ';
  }
};
export const getKYCStatusClassNameGetter = (data: any, field?: string) => {
  const value = data[field as keyof any];
  if (!isValidString(value)) {
    return 'text-yet-to-upload';
  }
  switch (value) {
    case KYC_DOCUMENT_APPROVAL_STATUS.YET_TO_UPLOAD.code:
      return 'text-yet-to-upload';
    case KYC_DOCUMENT_APPROVAL_STATUS.UPLOADED.code:
      return 'text-uploaded';
    case KYC_DOCUMENT_APPROVAL_STATUS.APPROVED.code:
      return 'text-completed';
    case KYC_DOCUMENT_APPROVAL_STATUS.REJECTED.code:
      return 'text-not-completed';
    default:
      return 'text-yet-to-upload';
  }
};

export const getKYCStatus = (data: any, field?: string) => {
  const value = data[field as keyof any];
  if (!isValidString(value)) {
    return KYC_DOCUMENT_APPROVAL_STATUS.YET_TO_UPLOAD.value;
  }
  switch (value) {
    case KYC_DOCUMENT_APPROVAL_STATUS.YET_TO_UPLOAD.code:
      return KYC_DOCUMENT_APPROVAL_STATUS.YET_TO_UPLOAD.value;
    case KYC_DOCUMENT_APPROVAL_STATUS.UPLOADED.code:
      return KYC_DOCUMENT_APPROVAL_STATUS.UPLOADED.value;
    case KYC_DOCUMENT_APPROVAL_STATUS.APPROVED.code:
      return KYC_DOCUMENT_APPROVAL_STATUS.APPROVED.value;
    case KYC_DOCUMENT_APPROVAL_STATUS.REJECTED.code:
      return KYC_DOCUMENT_APPROVAL_STATUS.REJECTED.value;
    default:
      return KYC_DOCUMENT_APPROVAL_STATUS.YET_TO_UPLOAD.value;
  }
};
export const getKYCStatusForGrid = (data: any, field?: string) => {
  const value = data[field as keyof any];
  if (!isValidString(value)) {
    return 'Not Completed';
  }
  if (value === KYC_DOCUMENT_APPROVAL_STATUS.APPROVED.code) {
    return 'Completed';
  } else {
    return 'Not Completed';
  }
};
export const getCompletedClassNameGetterKYCGrid = (
  data: any,
  field?: string
) => {
  const value = data[field as keyof any];
  if (!isValidString(value)) {
    return 'text-not-completed ';
  }
  if (value === KYC_DOCUMENT_APPROVAL_STATUS.APPROVED.code) {
    return 'text-completed ';
  } else {
    return 'text-not-completed ';
  }
};
export const getKYCActionText = (data: any, field?: string) => {
  const value = data[field as keyof any];
  if (!isValidString(value)) {
    return 'View Detail';
  }
  if (value === KYC_DOCUMENT_APPROVAL_STATUS.UPLOADED.code) {
    return 'Verification Pending';
  } else {
    return 'View Detail';
  }
};

export const getKYCAction = (data: any, field?: string) => {
  const value = data[field as keyof any];
  if (!isValidString(value)) {
    return CLICK_ACTIONS.KYC_VIEW_DETAILS;
  }
  if (value === KYC_DOCUMENT_APPROVAL_STATUS.UPLOADED.code) {
    return CLICK_ACTIONS.APPROVE;
  } else {
    return CLICK_ACTIONS.KYC_VIEW_DETAILS;
  }
};

export const capitalizeFirstLetter = (text?: string) => {
  if (!isValidString(text)) {
    return '-';
  }
  const value = text ?? '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const getNoteDate = (noteItem: NoteType) => {
  if (!noteItem || !(noteItem.createdon || noteItem.createdOn)) {
    return '';
  }
  return (
    moment(
      isValidString(noteItem.createdon)
        ? noteItem.createdon
        : noteItem.createdOn
    ).format('DD-MM-YYYY  hh:mm A') + '- '
  );
};

export const getUTCDate = (noteItem: NoteType) => {
  if (!noteItem || !(noteItem.createdon || noteItem.createdOn)) {
    return '';
  }

  const date = isValidString(noteItem.createdon)
    ? noteItem.createdon
    : noteItem.createdOn;

  return moment.utc(date).format('DD-MM-YYYY hh:mm A') + ' - ';
};

export const saveInLocalStorage = (key: string, value: string) => {
  localStorage.setItem(key, value);
};
export const removeFromLocalStorage = (key: string) => {
  localStorage.removeItem(key);
};
export const getFromLocalStorage = (key: string) => {
  const data = localStorage.getItem(key);
  return data ?? '';
};

export const getAccessToken = () => {
  const data = localStorage.getItem(STORAGE_KEY.AUTH_DATA);
  if (data) {
    const user: LoggedUserType = JSON.parse(data);
    return user.accessToken;
  }
  return '';
};

export const clearSession = () => {
  sessionStorage.clear();
};
export const getUser = (): LoggedUserType => {
  let user: LoggedUserType = loginMockData.user;
  const data = localStorage.getItem(STORAGE_KEY.AUTH_DATA);
  if (data) {
    user = JSON.parse(data);
  }
  return user;
};

export const getRoleClassName = (roleName: string) => {
  if (roleName === 'CEO' || roleName === 'BOD' || roleName === 'CGO') {
    return 'role-bg-cgo';
  } else if (roleName === 'Zonal Head') {
    return 'role-bg-zh';
  } else if (roleName === 'State Head') {
    return 'role-bg-sh';
  } else if (roleName === 'POSP - Manager') {
    return 'role-bg-posp-m';
  } else if (roleName === 'POSP') {
    return 'role-bg-posp';
  } else {
    return 'role-bg-other';
  }
};

export const getFormattedTime = (totalMinutes: number) => {
  if (!totalMinutes) {
    return '-';
  }
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);
  let time = '';
  if (hours) {
    if (hours === 1) {
      time = hours + ' hr';
    } else {
      time = hours + ' hrs';
    }
    if (minutes) {
      time = time + ', ' + minutes + ' mins';
    }
  } else {
    time = minutes + ' mins';
  }
  return time;
};

export const getPhotoSrc = (photoUrl?: string) => {
  const urlRegx = /^https?:\/\//;

  if (!photoUrl) return UserDefault;
  return urlRegx.test(photoUrl)
    ? photoUrl
    : `data:image/png;base64, ${photoUrl}`;
};
export const getDocumentPhotoSrc = (photoUrl: string | null) => {
  const urlRegx = /^https?:\/\//;
  if (!photoUrl || photoUrl === 'false' || photoUrl === 'null') return '';
  return urlRegx.test(photoUrl)
    ? photoUrl
    : `data:image/png;base64, ${photoUrl}`;
};
export const isValidDocumentURL = (photoUrl?: string | null) => {
  if (!photoUrl || photoUrl === 'false' || photoUrl === 'null') return false;
  else return true;
};
export const isDocumentAvailable = (documents?: DocumentType[]) => {
  let available = false;
  if (!documents || documents.length === 0) {
    return available;
  }
  for (const element of documents) {
    const document = element;
    if (
      (document.documentUrl && document.documentUrl !== 'false') ||
      (document.documentUrlBack && document.documentUrlBack !== 'false')
    ) {
      available = true;
      break;
    }
  }
  return available;
};
export const getDefaultImage = () => {
  return UserDefault;
};

const toRad = (value: number) => {
  return (value * Math.PI) / 180;
};
export const gatDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) => {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const latA1 = toRad(lat1);
  const latA2 = toRad(lat2);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(latA1) * Math.cos(latA2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d.toFixed(2) + ' kms';
};

export const isAPIFailed = (fulfilled: any) => {
  if (
    fulfilled?.responseInformation &&
    fulfilled?.responseInformation?.responseCode === API_RESPONSE_CODE.FAIL
  ) {
    return true;
  } else {
    return false;
  }
};
export const isAPISuccess = (fulfilled: any) => {
  if (
    fulfilled?.responseInformation &&
    fulfilled?.responseInformation?.responseCode === API_RESPONSE_CODE.SUCCESS
  ) {
    return true;
  } else {
    return false;
  }
};

export const toProperCase = (text: string) => {
  return text.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
  });
};

export const add3Dots = (text: string, limit: number) => {
  const dots = '...';
  if (!text) {
    return '-';
  }
  if (text.length > limit) {
    text = text.substring(0, limit) + dots;
  }
  return text;
};

export const canShowToolTip = (limit: number, data: any, field?: string) => {
  const email = data[field as keyof any];
  if (!email || email === 'null' || email.length <= limit) {
    return false;
  } else {
    return email.length > limit;
  }
};
export const getLatLngArrayFromCoordinate = (polygon: string) => {
  const latLngArray: any[] = [];
  if (!polygon) {
    return latLngArray;
  }
  let text = polygon.replace('POLYGON((', '');
  text = text.replace('))', '');
  const array = text.split(',');
  if (array) {
    array.forEach((item) => {
      const latLngNoSpaceArray = item.split(' ');
      const lngLngValue = toLatLon(
        Number(latLngNoSpaceArray[0]),
        Number(latLngNoSpaceArray[1]),
        undefined,
        'Q',
        true,
        44
      );
      if (lngLngValue) {
        latLngArray.push(lngLngValue);
      }
    });
  }
  return latLngArray;
};

export const getLatLngArray = (polygon: string) => {
  const latLngArray: any[] = [];
  if (!polygon) {
    return latLngArray;
  }
  let text = polygon.replace('MULTIPOLYGON(((', '');
  text = text.replace(')))', '');
  const array = text.split(',');
  if (array) {
    array.forEach((item) => {
      const latLngNoSpaceArray = item.split(' ');
      if (latLngNoSpaceArray) {
        latLngArray.push({
          lat: Number(latLngNoSpaceArray[1]),
          lng: Number(latLngNoSpaceArray[0]),
        });
      }
    });
  }
  return latLngArray;
};

export const getValueFromArrayObject = (
  array: any[] | undefined,
  keyName: string
) => {
  if (!array || array.length === 0) {
    return '-';
  }
  const noDuplicateArray: string[] = [];
  let text = '';
  for (const element of array) {
    const value = element[keyName];
    if (!noDuplicateArray.includes(value)) {
      noDuplicateArray.push(value);
    }
  }
  text = noDuplicateArray.join(', ');
  if (!text) {
    return '-';
  }
  const lastIndex = text.lastIndexOf(',');
  if (lastIndex > 0) {
    text = text.substring(0, lastIndex) + ' &' + text.substring(lastIndex + 1);
  }
  return text;
};

export const getValueFromArrayObjectGrid = (
  array: any[] | undefined,
  keyName: string
) => {
  if (!array || array.length === 0) {
    return '-';
  }

  const noDuplicateArray: string[] = [];

  for (const element of array) {
    const rawValue = element[keyName];
    if (rawValue) {
      const splitValues = rawValue.split(',').map((v: any) => v.trim());
      for (const val of splitValues) {
        if (!noDuplicateArray.includes(val)) {
          noDuplicateArray.push(val);
        }
      }
    }
  }

  if (noDuplicateArray.length === 0) {
    return '-';
  }

  const text = noDuplicateArray.join(', ');
  const lastIndex = text.lastIndexOf(',');
  return lastIndex > 0
    ? text.substring(0, lastIndex) + ' &' + text.substring(lastIndex + 1)
    : text;
};

/**
 * Prepare Campaign details for candidate
 */
export const findCampaignDetails = (
  campaignDetailsInfo: CampaignDetailType[]
) => {
  const campaignDetailObj: any = {
    campaignName: [],
    referralName: '',
    othersDes: '',
  };
  campaignDetailsInfo?.forEach((campaignItem) => {
    if (campaignItem.campaignname) {
      campaignDetailObj.campaignName.push(campaignItem.campaignname);
    }
    if (campaignItem.campaignname === 'Others') {
      campaignDetailObj.othersDes = addSeparator(
        campaignDetailObj.othersDes,
        getBlankValueIfNUll(campaignItem.descriptona)
      );
    }
    if (campaignItem.campaignname === 'Referral') {
      const descA = getBlankValueIfNUll(campaignItem.descriptona);
      const descB = getBlankValueIfNUll(campaignItem.descriptionb);
      if (descA && descB) {
        campaignDetailObj.referralName = addSeparator(
          campaignDetailObj.referralName,
          descA + ' & ' + descB
        );
      } else if (descA) {
        campaignDetailObj.referralName = addSeparator(
          campaignDetailObj.referralName,
          descA
        );
      } else if (descB) {
        campaignDetailObj.referralName = addSeparator(
          campaignDetailObj.referralName,
          descB
        );
      }
    }
  });
  return campaignDetailObj;
};

// based on the approval status we are changing the colors
export const handleViewAttendeesActionColors = (data: any) => {
  const value = data.approvalStatus;
  if (value == ATTENDEES_STATUS?.REJECTED) {
    return 'text-danger';
  } else if (
    value == ATTENDEES_STATUS?.APPROVED ||
    value == ATTENDEES_STATUS?.MARKED
  ) {
    return 'text-success';
  } else {
    return 'hide-text-decoration text-decoration-none text-primary';
  }
};

export const handleLeaveHistoryActionColors = (data: any) => {
  const value = data?.requestStatus;
  if (value == LEAVE_STATUSES?.REJECT) {
    return 'text-danger';
  } else if (value == LEAVE_STATUSES?.APPROVE) {
    return 'text-success';
  } else {
    return '';
  }
};

export const handleApproveReimbursementActionColors = (data: any) => {
  const value = data?.statusId;
  if (value == REIMBURSEMENT_STATUS_TYPES?.REJECT) {
    return 'text-danger';
  } else if (value == REIMBURSEMENT_STATUS_TYPES?.APPROVED) {
    return 'text-primary';
  } else {
    return '';
  }
};

export const approveReimbursementActionGetter = (data: any) => {
  const value = data?.statusId;
  if (REIMBURSEMENT_STATUS_TYPES?.APPROVED == value) {
    return CLICK_ACTIONS.NO_ACTION;
  } else {
    return CLICK_ACTIONS.APPROVE;
  }
};

export const prepareDataToFindZoneAndState = (
  user: LoggedUserType | null,
  userDetails: UserType | null
) => {
  let data: ZoneRequestObj | null = null;
  if (user && userDetails?.candidateid) {
    data = {
      zoneId: [],
      stateId: [],
    };

    if (user.roleId && PINCODES_ACCESS_ROLES.includes(user.roleId)) {
      // find zone and state and district in single api.
      if (userDetails.reportingZones) {
        data.zoneId = userDetails.reportingZones;
      }
      if (userDetails.reportingStates) {
        data.stateId = userDetails.reportingStates;
      }
    } else if (user.roleId && DISTRICT_ACCESS_ROLES.includes(user.roleId)) {
      // find zone and state and district in single api.
      if (userDetails.reportingZones) {
        data.zoneId = userDetails.reportingZones;
      }
      if (userDetails.reportingStates) {
        data.stateId = userDetails.reportingStates;
      }
    } else if (user.roleId && STATE_ACCESS_ROLES.includes(user.roleId)) {
      // find zone and state and district in single api.
      if (userDetails.reportingZones) {
        data.zoneId = userDetails.reportingZones;
      }
      if (userDetails.reportingStates) {
        data.stateId = userDetails.reportingStates;
      }
    } else if (user.roleId && ZONE_ACCESS_ROLES.includes(user.roleId)) {
      // find zone and state in single api and all district of state in another api
      if (userDetails.reportingZones) {
        data.zoneId = userDetails.reportingZones;
      }
    }
  }
  return data;
};

export const prepareDataToFindCity = (
  user: LoggedUserType | null,
  userDetails: UserType | null,
  statesData: MapOptionType | null,
  districtData: MapOptionType | null
) => {
  let data: ZoneRequestObj | null = null;
  if (
    user &&
    userDetails?.candidateid &&
    statesData &&
    !STATE_ACCESS_ROLES.includes(user.roleId) &&
    districtData &&
    !Object.keys(districtData)?.length
  ) {
    data = {
      zoneId: [],
      stateId: [],
    };
    if (userDetails.reportingZones && userDetails.reportingZones.length > 0) {
      data.zoneId = userDetails.reportingZones;
    } else {
      data.zoneId = Object.keys(statesData);
    }
    const zoneIdsKeys: string[] | number[] = data.zoneId;
    const stateIds: number[] = [];
    for (let index = 0; index < zoneIdsKeys?.length; index++) {
      const stateArray: SelectOptionType[] = statesData[zoneIdsKeys[index]];
      stateArray.map((element: SelectOptionType) => {
        stateIds.push(Number(element.value));
      });
    }
    data.stateId = stateIds;
  }
  return data;
};
export const getTableItemClickClass = (
  item: any,
  headerItem: ColumnHeaderType
) => {
  if (
    headerItem.actionGetter &&
    headerItem.actionGetter(item, headerItem.field) === CLICK_ACTIONS.NO_ACTION
  ) {
    return '';
  } else if (
    (headerItem.action ?? headerItem.actionGetter) &&
    headerItem.clickable
  ) {
    return 'text-decoration-underline td-clickable cursor-pointer';
  } else {
    return '';
  }
};
export const viewAttendanceActionGetter = (data: any) => {
  const value = data?.approvalStatus;
  if (ATTENDEES_STATUS?.SYSTEM_APPROVED == value) {
    return CLICK_ACTIONS.NO_ACTION;
  } else {
    return CLICK_ACTIONS.APPROVE;
  }
};

export const getLocationValues = (
  name: string,
  value: any,
  locationData: LocationFilterInitialState,
  parentId?: any
) => {
  if (name === 'zone') {
    const zoneData = locationData.zoneOptions.find(
      (zone) => zone.value == value
    );
    return zoneData?.label ?? '-';
  } else if (name === 'state' && parentId) {
    const stateData = locationData?.statesData?.[parentId]?.find(
      (state) => state.value == value
    );
    return stateData?.label ?? '-';
  } else if (name === 'district' && parentId) {
    const districtData = locationData?.districtData?.[parentId]?.find(
      (district) => district.value == value
    );
    return districtData?.label ?? '-';
  } else if (name === 'city' && parentId) {
    const cityData = locationData?.cityOptions?.[parentId]?.find(
      (city) => city.value == value
    );
    return cityData?.label ?? '-';
  }
  return '-';
};

export const formatNumberWithComma = (value: number) => {
  if (value) {
    return Number(value).toLocaleString(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  }
  return '-';
};

export const convertDateToISODate = (date: string | undefined) => {
  if (date) {
    return new Date(
      moment(date, 'DD-MM-YYYY H:mm').format('DD MMM YYYY H:mm')
    ).toISOString();
  }
  return null;
};

export const addFourDigitsToTheId = (num: number | string) => {
  if (num) {
    return num?.toString()?.padStart(4, '0');
  }
  return '-';
};

export const findDataFromArray = (
  array: SelectOptionType[],
  value: string | number
) => {
  for (const element of array) {
    if (
      Number(element.value) === Number(value) ||
      String(element.value) === String(value)
    ) {
      return element.label;
    }
  }
};
export const roleValueGetter = (data: any, field?: string) => {
  let roles = data[field as keyof any];
  if (!isValidString(roles)) {
    return '-';
  }
  const index = roles.lastIndexOf(',');
  if (index > 0) {
    roles = replaceStringAt(roles, index, ' & ');
  }
  return roles;
};
export const roleCountValueGetter = (data: any, field?: string) => {
  const roles = data[field as keyof any];
  if (!isValidString(roles)) {
    return 0;
  }
  return roles.split(',').length;
};

export const processDistricts = (districts: any[]) => {
  const districtOption: SelectOptionType[] = [];
  districts.forEach((district: any) => {
    districtOption.push({
      value: district.districtId,
      label: district.districtName,
    });
  });
  return districtOption;
};
export const handleDropOptions = (
  list: any[],
  keyId: string,
  keyName: string
) => {
  if (list?.length) {
    return list.map((item: any) => ({
      value: item[keyId],
      label: item[keyName],
    }));
  }
  return [];
};
export const getTaskName = (data: any) => {
  switch (data?.referenceModuleName) {
    case 'Task':
      return `- ${data.referenceType}`;
    case 'Logged':
      return '- Logged In';
    default:
      return '-';
  }
};
